import { useCallback, useRef, useState } from 'react';
import { createPopper } from '@popperjs/core';
import { ReactComponent as CalendarIcon } from 'assets/calendar.svg';
import { useModifiedTranslation } from 'hooks/useModifiedTranslation';
import ReactDatePicker, { DatePickerProps as ReactDatePickerProps } from 'react-date-picker/dist/entry.nostyle';
import { announceToScreenReader } from 'utils/accessibility';
import './DatePicker.css';
import './Calendar.css';

// Simple counter for generating unique IDs when none provided
let idCounter = 0;

interface DatePickerProps extends ReactDatePickerProps {
    label?: string;
    id?: string;
}

export const DatePicker = ({ label, id: providedId, onChange, value, ...props }: DatePickerProps) => {
    const { i18n, t } = useModifiedTranslation();
    const idRef = useRef(providedId || `datepicker-${++idCounter}`);
    const id = idRef.current;
    const { positionedElementRefCallback, targetRefCallback } = usePositionElementToOtherElement();
    const locale = i18n.language === i18n.resolvedLanguage ? i18n.language : i18n.resolvedLanguage;
    
    // Wraps onChange to announce the selected date to screen readers
    const handleDateChange = useCallback((newValue: Date | Date[] | null) => {
        if (newValue instanceof Date) {
            const formattedDate = newValue.toLocaleDateString(locale, {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
            announceToScreenReader(t('accessibility.dateSelected', { date: formattedDate }));
        }
        
        if (onChange) {
            (onChange as (value: Date | Date[] | null) => void)(newValue);
        }
    }, [onChange, locale, t]);
    
    return (
        <div className='flex flex-col gap-0 w-full' ref={targetRefCallback}>
            {label && <label htmlFor={id} className='mb-1 text-sm font-normal'>{label}</label>}
            <ReactDatePicker
                calendarIcon={<CalendarIcon />}
                clearIcon={null}
                className='px-2 pt-2 pb-1.5 text-base font-normal text-gray-500 bg-gray-50 rounded-lg border border-gray-300'
                locale={locale}
                showLeadingZeros
                inputRef={positionedElementRefCallback}
                calendarAriaLabel={label || 'Select date'}
                dayAriaLabel='Day'
                monthAriaLabel='Month'
                yearAriaLabel='Year'
                nativeInputAriaLabel={label || 'Date'}
                value={value}
                onChange={handleDateChange}
                {...props}
            />
        </div>
    );
};

const usePositionElementToOtherElement = () => {
    const [targetNode, setTargetNode] = useState<HTMLElement>();

    const targetRefCallback = useCallback((node: HTMLElement | null) => {
        if (!node) {
            return;
        }
        setTargetNode(node);
    }, []);

    const positionedElementRefCallback = useCallback(
        (node: HTMLElement | null) => {
            if (!node || !targetNode) {
                return;
            }
            createPopper(targetNode, node, {
                placement: 'bottom-start',
            });
        },
        [targetNode]
    );

    return {
        positionedElementRefCallback,
        targetRefCallback,
    };
};
